<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { formatDate } from 'dashboard/helper/dateHelper.js';
import ClassificationBadge from './ClassificationBadge.vue';
import CommentTypeBadge from './CommentTypeBadge.vue';
import SourceTypeBadge from './SourceTypeBadge.vue';
import Icon from 'next/icon/Icon.vue';
import MetadataBadge from './MetadataBadge.vue';
import Reply from './Reply.vue';
import CustomClassificationBadge from './CustomClassificationBadge.vue';
import ReplyDialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  comment: {
    type: Object,
    required: true,
    validator(value) {
      return (
        typeof value.id === 'number' &&
        typeof value.from_id === 'string' &&
        typeof value.from_name === 'string' &&
        typeof value.message === 'string' &&
        typeof value.created_time === 'string' &&
        typeof value.hidden === 'boolean' &&
        (typeof value.classification === 'string' ||
          value.classification === null) &&
        Array.isArray(value.replies) &&
        typeof value.post === 'object' &&
        typeof value.post.id === 'number' &&
        typeof value.post.source_type === 'string'
      );
    },
  },
  uiFlags: {
    type: Object,
    required: true,
  },
  selected: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'hidden',
  'deleted',
  'select',
  'responded',
  'replyFailed',
  'checkCommentDeleted',
]);

const store = useStore();
const { t } = useI18n();

const replyDialog = ref(null);
const replyText = ref('');
const replyTargetAuthor = ref('');
const replyTargetId = ref(null);
const isSubmittingReply = ref(false);

const dmDialog = ref(null);
const dmText = ref('');
const isSubmittingDm = ref(false);

const showReplies = ref(false);
const moderationInFlight = ref(false);

const isCardInteractionLocked = computed(
  () => props.uiFlags.isFetching || moderationInFlight.value
);

const calculateTimeWithoutReply = computed(() => {
  const commentDate = new Date(props.comment.created_time);
  const currentDate = new Date();
  const diff = currentDate - commentDate;
  const diffInHours = diff / (1000 * 60 * 60);
  return diffInHours < 24
    ? `${Math.floor(diffInHours)} horas`
    : `${Math.floor(diffInHours / 24)} dias`;
});

const postType = computed(() => {
  return type => {
    switch (type) {
      case null:
        return 'FEED';
      default:
        return type;
    }
  };
});

const metadataType = computed(() => {
  return metadata => {
    switch (metadata?.purchase_intention) {
      case true:
        return 'PURCHASE_INTENTION';
      default:
        return metadata;
    }
  };
});

const showMetadataBadge = computed(() => {
  return (
    !!props.comment.from_name && !!props.comment.metadata?.purchase_intention
  );
});

const isMentionedComment = computed(() => {
  return props.comment.metadata?.type === 'MENTION';
});

const closeReplyPopup = () => {
  replyText.value = '';
};

const verifyCommentExists = async commentId => {
  try {
    await store.dispatch('comments/verifyCommentExists', {
      commentId,
      pageId: props.comment.facebook_page.id,
      postId: props.comment.post.id,
    });
    return { exists: true };
  } catch (error) {
    // Handle 410 (Gone) - comment deleted on platform
    if (error.response?.status === 410 && error.response?.data?.deleted) {
      return {
        exists: false,
        deletedCommentId: error.response.data.comment_id || commentId,
        error: error.response.data.error,
      };
    }
    // Handle 404 (Not Found) - comment doesn't exist in local database
    if (error.response?.status === 404 && error.response?.data?.deleted) {
      return {
        exists: false,
        deletedCommentId: error.response.data.comment_id || commentId,
        error: error.response.data.error || 'Comment not found',
      };
    }
    // For other errors, assume comment exists (don't block user)
    return { exists: true };
  }
};

const openReplyPopup = async payloadOrId => {
  if (isCardInteractionLocked.value) return;
  let author = null;
  let targetId = null;
  if (payloadOrId && typeof payloadOrId === 'object') {
    author = payloadOrId.authorName || null;
    targetId = payloadOrId.replyId ?? null;
  }

  const commentIdToCheck = targetId || props.comment.id;

  // Check if comment exists before opening reply modal
  // Always check, even if user previously canceled deletion confirmation
  try {
    const verification = await verifyCommentExists(commentIdToCheck);

    if (!verification.exists) {
      // Comment was deleted, emit event to show confirmation modal
      emit('checkCommentDeleted', {
        commentId: props.comment.id,
        deletedCommentId: verification.deletedCommentId || commentIdToCheck,
        error: verification.error,
        action: 'reply',
        isMentioned: props.comment.metadata?.type === 'MENTION',
        pageId: props.comment.facebook_page?.id,
        postId: props.comment.post?.id,
      });
      return;
    }
  } catch (error) {
    if (error.response?.status === 410 && error.response?.data?.deleted) {
      // Comment was deleted or not found, emit event to show confirmation modal
      emit('checkCommentDeleted', {
        commentId: props.comment.id,
        deletedCommentId: error.response.data.comment_id || commentIdToCheck,
        error: error.response.data.error,
        action: 'reply',
        isMentioned: props.comment.metadata?.type === 'MENTION',
        pageId: props.comment.facebook_page?.id,
        postId: props.comment.post?.id,
      });
      return;
    }
    // For other errors, proceed anyway (don't block user)
  }

  replyTargetAuthor.value = author || props.comment.from_name || '';
  replyTargetId.value = targetId;
  replyText.value = '';
  replyDialog.value?.open();
};

const submitReply = async () => {
  isSubmittingReply.value = true;
  try {
    const isFacebook = props.comment.post?.source_type === 'FACEBOOK';
    const username =
      replyTargetAuthor.value ||
      props.comment.from_name ||
      props.comment.inbox?.name ||
      '';
    let mention = '';
    if (username) {
      mention = isFacebook ? `${username} ` : `@${username} `;
    }
    const messageWithMention = `${mention}${replyText.value}`.trim();

    await store.dispatch('comments/createReply', {
      pageId: props.comment.facebook_page.id,
      postId: props.comment.post.id,
      commentId: props.comment.id,
      message: messageWithMention,
      callback: ({ success, deleted, commentId: deletedCommentId, error }) => {
        if (success) {
          replyText.value = '';
          replyDialog.value?.close();
          replyTargetAuthor.value = '';
          emit('responded', props.comment.id);
        } else if (deleted) {
          replyText.value = '';
          replyDialog.value?.close();
          const eventData = {
            commentId: props.comment.id,
            deletedCommentId: deletedCommentId || props.comment.id,
            error,
            isMentioned: props.comment.metadata?.type === 'MENTION',
          };
          emit('replyFailed', eventData);
        }
      },
    });
  } catch (error) {
    // If error wasn't handled by callback (non-410 errors), show alert
    if (error.response?.status !== 410) {
      // Error handling for non-410 errors
    }
  } finally {
    isSubmittingReply.value = false;
  }
};

const deleteComment = async (replyId = null) => {
  const targetId = replyId || props.comment.id;

  // Check if comment exists on Instagram before deleting
  try {
    const verification = await verifyCommentExists(targetId);

    if (!verification.exists) {
      // Comment was deleted on Instagram, show confirmation modal
      emit('checkCommentDeleted', {
        commentId: props.comment.id,
        deletedCommentId: verification.deletedCommentId || targetId,
        error: verification.error,
        action: 'delete',
        isMentioned: props.comment.metadata?.type === 'MENTION',
        pageId: props.comment.facebook_page?.id,
        postId: props.comment.post?.id,
      });
      return;
    }
  } catch (error) {
    // If verification fails for other reasons, proceed with deletion anyway
  }

  if (moderationInFlight.value) return;
  moderationInFlight.value = true;
  try {
    await store.dispatch('comments/delete', {
      commentId: targetId,
      pageId: props.comment.facebook_page.id,
      postId: props.comment.post.id,
      callback: ({ success, deleted, commentId: deletedCommentId, error }) => {
        if (success) {
          emit('deleted', {
            commentId: props.comment.id,
            replyId: replyId || null,
            deleted,
            deletedCommentId: deletedCommentId || targetId,
            error,
          });
        }
      },
    });
  } finally {
    moderationInFlight.value = false;
  }
};

const hideComment = async (replyId = null) => {
  const targetId = replyId || props.comment.id;

  // Check if comment exists before hiding
  try {
    const verification = await verifyCommentExists(targetId);

    if (!verification.exists) {
      // Comment was deleted, emit event to show confirmation modal
      emit('checkCommentDeleted', {
        commentId: props.comment.id,
        deletedCommentId: verification.deletedCommentId || targetId,
        error: verification.error,
        action: 'hide',
        isMentioned: props.comment.metadata?.type === 'MENTION',
        pageId: props.comment.facebook_page?.id,
        postId: props.comment.post?.id,
      });
      return;
    }
  } catch (error) {
    // If verification fails for other reasons, proceed anyway
  }

  if (moderationInFlight.value) return;
  moderationInFlight.value = true;
  try {
    await store.dispatch('comments/hide', {
      commentId: targetId,
      pageId: props.comment.facebook_page.id,
      postId: props.comment.post.id,
      callback: ({ success, deleted, commentId: deletedCommentId, error }) => {
        if (success) {
          emit('hidden', {
            commentId: props.comment.id,
            replyId: replyId || null,
            deleted,
            deletedCommentId: deletedCommentId || targetId,
            error,
          });
        }
      },
    });
  } finally {
    moderationInFlight.value = false;
  }
};

const toggleReplies = () => {
  if (isCardInteractionLocked.value) return;
  showReplies.value = !showReplies.value;
};

const handleCardClick = event => {
  if (isCardInteractionLocked.value) {
    event.preventDefault();
    event.stopPropagation();
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  emit('select', props.comment);
};

const openDmPopup = async () => {
  if (isCardInteractionLocked.value) return;

  // Check if comment exists before opening DM modal
  const verification = await verifyCommentExists(props.comment.id);

  if (!verification.exists) {
    // Comment was deleted, emit event to show confirmation modal
    emit('checkCommentDeleted', {
      commentId: props.comment.id,
      deletedCommentId: verification.deletedCommentId || props.comment.id,
      error: verification.error,
      action: 'dm',
      isMentioned: props.comment.metadata?.type === 'MENTION',
      pageId: props.comment.facebook_page?.id,
      postId: props.comment.post?.id,
    });
    return;
  }

  dmText.value = '';
  dmDialog.value?.open();
};

const closeDmPopup = () => {
  dmText.value = '';
};

const submitDm = async () => {
  isSubmittingDm.value = true;
  try {
    const result = await store.dispatch('comments/sendDirectMessage', {
      commentId: props.comment.id,
      message: dmText.value,
    });

    dmText.value = '';
    dmDialog.value?.close();

    useAlert(t('COMMENTS.MODERATION_CARD.DM_SENT_SUCCESS'));

    const accountId = store.getters.getCurrentAccountId;
    window.open(
      `/app/accounts/${accountId}/conversations/${result.conversation_id}`,
      '_blank'
    );
  } catch (error) {
    useAlert(t('COMMENTS.MODERATION_CARD.DM_SENT_ERROR'));
  } finally {
    isSubmittingDm.value = false;
  }
};
</script>

<template>
  <div
    class="relative bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700/50 overflow-hidden transition-all duration-200 hover:shadow-md"
    :class="{
      'ring-2 ring-woot-500 border-woot-500 shadow-lg': selected,
      'opacity-60': comment.hidden,
    }"
    @click="handleCardClick"
  >
    <div
      v-if="isCardInteractionLocked"
      class="absolute inset-0 z-[1] flex items-center justify-center rounded-xl bg-white/70 dark:bg-slate-900/60 pointer-events-auto"
      role="status"
      :aria-label="$t('COMMENTS.MODERATION_CARD.ACTIONS_PENDING')"
    >
      <Icon
        icon="i-lucide-loader-2"
        class="size-9 text-woot-500 motion-safe:animate-spin"
      />
    </div>
    <div
      v-if="comment.post"
      class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-3 px-4 lg:px-6 py-3 bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800/30 border-b border-slate-200 dark:border-slate-700/50"
    >
      <div class="flex flex-wrap items-center gap-2">
        <SourceTypeBadge
          :type="comment.post.source_type"
          :brand-name="comment.inbox.name"
        />
        <CommentTypeBadge :type="postType(comment.post.media_product_type)" />
      </div>
      <div
        class="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400"
      >
        <Icon icon="i-lucide-calendar" class="size-3.5 flex-shrink-0" />
        <span class="font-medium">
          {{ formatDate(comment.post.created_at) }}
        </span>
      </div>
    </div>

    <div
      class="flex flex-col sm:flex-row items-start gap-3 sm:gap-4 px-4 lg:px-6 py-4 sm:py-5"
    >
      <div
        class="flex-shrink-0 rounded-full bg-gradient-to-br from-woot-400 to-woot-600 shadow-md h-10 w-10 sm:h-12 sm:w-12 flex justify-center items-center"
      >
        <Icon icon="i-lucide-user" class="size-5 sm:size-6 text-white" />
      </div>

      <div class="flex-1 min-w-0 space-y-3">
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2 flex-wrap">
            <h3
              class="text-sm sm:text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2"
            >
              <span class="truncate max-w-[200px] sm:max-w-none">{{
                comment.from_name || comment.inbox.name
              }}</span>
              <a
                v-if="
                  comment.post.source_type === 'INSTAGRAM' && comment.from_name
                "
                :href="`https://www.instagram.com/${encodeURIComponent(comment.from_name.trim())}/`"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center text-woot-500 opacity-80 hover:opacity-100 hover:scale-110 transition-all flex-shrink-0"
                :class="{
                  'pointer-events-none opacity-50': isCardInteractionLocked,
                }"
                :aria-label="
                  $t('COMMENTS.MODERATION_CARD.OPEN_INSTAGRAM_PROFILE')
                "
                :title="$t('COMMENTS.MODERATION_CARD.VIEW_ON_INSTAGRAM')"
                @click.stop
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <defs>
                    <linearGradient
                      id="instagramGradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="100%"
                    >
                      <stop offset="0%" stop-color="#fccc63" />
                      <stop offset="20%" stop-color="#fbad50" />
                      <stop offset="40%" stop-color="#e95950" />
                      <stop offset="60%" stop-color="#d53f8c" />
                      <stop offset="80%" stop-color="#a437b4" />
                      <stop offset="100%" stop-color="#833ab4" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5z"
                    stroke="url(#instagramGradient)"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="5"
                    stroke="url(#instagramGradient)"
                    stroke-width="1.8"
                  />
                  <circle
                    cx="17"
                    cy="7"
                    r="1.3"
                    fill="url(#instagramGradient)"
                  />
                </svg>
              </a>
              <a
                v-if="
                  comment.post.source_type === 'FACEBOOK' && comment.source_id
                "
                :href="`https://www.facebook.com/${comment.hidden ? comment.source_id.split('_')[0] : comment.source_id}`"
                target="_blank"
                rel="noopener noreferrer"
                class="inline-flex items-center text-woot-500 opacity-80 hover:opacity-100 hover:scale-110 transition-all flex-shrink-0"
                :class="{
                  'pointer-events-none opacity-50': isCardInteractionLocked,
                }"
                :aria-label="
                  $t('COMMENTS.MODERATION_CARD.OPEN_FACEBOOK_COMMENT')
                "
                :title="$t('COMMENTS.MODERATION_CARD.VIEW_ON_FACEBOOK')"
                @click.stop
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3V2z"
                    stroke="#1877F2"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </a>
            </h3>
            <span
              v-if="comment.hidden"
              class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300"
            >
              <Icon icon="i-lucide-eye-off" class="size-3" />
              {{ $t('COMMENTS.MODERATION_CARD.HIDDEN') }}
            </span>
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <ClassificationBadge :type="comment.classification ?? 'unknown'" />
            <MetadataBadge
              v-if="showMetadataBadge"
              :type="metadataType(comment.metadata)"
            />
            <CustomClassificationBadge
              v-if="
                Array.isArray(comment.metadata?.custom_classifications) &&
                comment.metadata.custom_classifications.length
              "
              :items="comment.metadata.custom_classifications"
              :max-visible="3"
            />
          </div>
        </div>

        <div
          class="bg-slate-50 dark:bg-slate-900/50 rounded-lg p-3 sm:p-4 border border-slate-200 dark:border-slate-700/50"
        >
          <p
            class="text-sm sm:text-base text-slate-700 dark:text-slate-300 break-words whitespace-pre-wrap leading-relaxed"
          >
            {{ comment.message }}
          </p>
        </div>

        <div
          class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2"
        >
          <div class="flex items-center gap-3 flex-wrap">
            <div
              class="flex items-center gap-1.5 text-xs sm:text-sm text-slate-500 dark:text-slate-400"
            >
              <Icon icon="i-lucide-clock" class="size-3.5" />
              <span>{{ formatDate(comment.created_time) }}</span>
            </div>
            <span
              v-if="calculateTimeWithoutReply && !comment.replies?.length"
              class="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs sm:text-sm bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 font-medium"
            >
              <Icon icon="i-lucide-alert-circle" class="size-3.5" />
              {{
                $t('COMMENTS.MODERATION_CARD.TIME_WITHOUT_REPLY', {
                  time: calculateTimeWithoutReply,
                })
              }}
            </span>
          </div>

          <div class="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-woot-600 dark:text-woot-400 bg-woot-50 dark:bg-woot-900/20 hover:bg-woot-100 dark:hover:bg-woot-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
              :disabled="isCardInteractionLocked"
              @click.stop="openReplyPopup()"
            >
              <Icon icon="i-lucide-reply" class="size-3.5" />
              {{ $t('COMMENTS.MODERATION_CARD.REPLY') }}
            </button>
            <button
              type="button"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
              :disabled="isCardInteractionLocked"
              @click.stop="openDmPopup()"
            >
              <Icon icon="i-lucide-mail" class="size-3.5" />
              {{ $t('COMMENTS.MODERATION_CARD.SEND_DM') }}
            </button>
            <button
              v-if="!isMentionedComment"
              type="button"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
              :disabled="isCardInteractionLocked"
              @click.stop="deleteComment()"
            >
              <Icon icon="i-lucide-trash-2" class="size-3.5" />
              {{ $t('COMMENTS.MODERATION_CARD.DELETE') }}
            </button>
            <button
              v-if="!isMentionedComment"
              type="button"
              class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-woot-600 dark:hover:text-woot-400 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
              :disabled="isCardInteractionLocked"
              @click.stop="hideComment()"
            >
              <Icon
                :icon="comment.hidden ? 'i-lucide-eye' : 'i-lucide-eye-off'"
                class="size-3.5"
              />
              {{
                comment.hidden
                  ? $t('COMMENTS.MODERATION_CARD.SHOW')
                  : $t('COMMENTS.MODERATION_CARD.HIDE')
              }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="comment.replies && comment.replies.length > 0"
      class="border-t border-slate-200 dark:border-slate-700/50"
    >
      <div class="px-4 lg:px-6 py-3 bg-slate-50/50 dark:bg-slate-800/30">
        <button
          type="button"
          class="inline-flex items-center gap-2 text-sm font-medium text-woot-600 dark:text-woot-400 hover:text-woot-700 dark:hover:text-woot-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
          :disabled="isCardInteractionLocked"
          @click.stop="toggleReplies()"
        >
          <Icon
            :icon="
              showReplies ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
            "
            class="size-4 transition-transform"
            :class="{ 'rotate-180': !showReplies }"
          />
          <span>
            {{
              showReplies
                ? $t('COMMENTS.MODERATION_CARD.HIDE_REPLIES')
                : $t('COMMENTS.MODERATION_CARD.VIEW_REPLIES', {
                    count: comment.replies.length,
                  })
            }}
          </span>
          <span
            class="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold bg-woot-100 dark:bg-woot-900/40 text-woot-700 dark:text-woot-300"
          >
            {{ comment.replies.length }}
          </span>
        </button>
      </div>

      <div
        v-if="showReplies"
        class="px-4 lg:px-6 pb-4 space-y-3 bg-gradient-to-b from-slate-50/50 to-transparent dark:from-slate-800/20 dark:to-transparent"
      >
        <div
          v-for="reply in comment.replies"
          :key="reply.id"
          class="pl-4 sm:pl-8 lg:pl-12 border-l-2 border-woot-200 dark:border-woot-800"
        >
          <Reply
            :reply="reply"
            :post-source-type="comment.post.source_type"
            :ui-flags="uiFlags"
            :parent-moderation-busy="isCardInteractionLocked"
            :hide-comment="() => hideComment(reply.id)"
            :delete-comment="() => deleteComment(reply.id)"
            @open-reply-popup="openReplyPopup"
          />
        </div>
      </div>
    </div>

    <ReplyDialog
      ref="replyDialog"
      type="edit"
      :title="
        $t('COMMENTS.MODERATION_CARD.REPLY_TO', {
          name: replyTargetAuthor || comment.from_name,
        })
      "
      :confirm-button-label="$t('COMMENTS.MODERATION_CARD.SEND')"
      :cancel-button-label="$t('COMMENTS.MODERATION_CARD.CANCEL')"
      :disable-confirm-button="!replyText.trim() || replyText.length > 500"
      :is-loading="isSubmittingReply"
      width="lg"
      @confirm="submitReply"
      @close="closeReplyPopup"
    >
      <TextArea
        v-model="replyText"
        :placeholder="$t('COMMENTS.MODERATION_CARD.REPLY_PLACEHOLDER')"
        :max-length="500"
        :disabled="isSubmittingReply"
        show-character-count
        auto-height
        autofocus
        min-height="8rem"
        max-height="16rem"
      />
    </ReplyDialog>

    <ReplyDialog
      ref="dmDialog"
      type="edit"
      :title="
        $t('COMMENTS.MODERATION_CARD.SEND_DM_TO', {
          name: comment.from_name,
        })
      "
      :confirm-button-label="$t('COMMENTS.MODERATION_CARD.SEND')"
      :cancel-button-label="$t('COMMENTS.MODERATION_CARD.CANCEL')"
      :disable-confirm-button="!dmText.trim() || dmText.length > 1000"
      :is-loading="isSubmittingDm"
      width="lg"
      @confirm="submitDm"
      @close="closeDmPopup"
    >
      <TextArea
        v-model="dmText"
        :placeholder="$t('COMMENTS.MODERATION_CARD.DM_PLACEHOLDER')"
        :max-length="1000"
        :disabled="isSubmittingDm"
        show-character-count
        auto-height
        autofocus
        min-height="8rem"
        max-height="16rem"
      />
    </ReplyDialog>
  </div>
</template>
